/**
 * =============================================================================
 * Hook: useAffiliatePayouts
 * Versão: 1.0.0 (Fase 3 — Bloco 3D)
 * Descrição: Lista o histórico de saques (payouts) do afiliado ativo.
 *   - Busca via Supabase client (RLS no banco filtra por affiliate_id).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useAffiliatePayouts');

export type PayoutStatus = Database['public']['Enums']['affiliate_payout_status'];
export type PayoutMethod = Database['public']['Enums']['affiliate_payout_method'];

/**
 * Anexo (comprovante) retornado embutido em cada payout pela RPC
 * `list_affiliate_payouts_paged`. Subset enxuto, suficiente para renderizar
 * miniaturas via `AttachmentThumbnail`.
 */
export interface PayoutAttachmentLite {
  id: string;
  storage_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
}

export interface AffiliatePayoutRow {
  id: string;
  amount: number;
  currency: string;
  status: PayoutStatus;
  payout_method: PayoutMethod;
  commissions_count: number;
  requested_at: string;
  processed_at: string | null;
  paid_at: string | null;
  failed_reason: string | null;
  notes: string | null;
  /** Comprovantes anexados ao saque (preenchido server-side, evita N+1). */
  attachments: PayoutAttachmentLite[];
}

export function useAffiliatePayouts() {
  const { affiliate, ready } = useAffiliateAuth();

  return useQuery({
    queryKey: ['affiliate-payouts', affiliate?.id],
    enabled: ready && !!affiliate?.id,
    queryFn: async (): Promise<AffiliatePayoutRow[]> => {
      // Acesso direto a affiliate_payouts foi bloqueado pelo hardening RLS.
      // Toda leitura passa pela RPC `list_affiliate_payouts_paged`.
      const { data, error } = await supabase.rpc('list_affiliate_payouts_paged', {
        p_limit: 100,
        p_offset: 0,
      });

      if (error) {
        log.error('Falha ao listar payouts via RPC', error);
        throw error;
      }

      const payload = (data as unknown as { items?: AffiliatePayoutRow[] } | null) ?? null;
      return (payload?.items ?? []).map((p) => ({
        ...p,
        amount: Number(p.amount ?? 0),
        commissions_count: Number(p.commissions_count ?? 0),
        // Garante array (em payouts antigos sem anexos a RPC retorna [])
        attachments: Array.isArray(p.attachments) ? p.attachments : [],
      }));
    },
    staleTime: 30_000,
  });
}
